import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import block from 'bem-cn'

import { MODAL_ROUTE_PATH, MODAL_ROUTE_URLS } from 'router/modalRoutes'
import { ROUTE_URLS } from 'router/routes'

import { ReactComponent as Arrow } from 'shared/img/arrow-left.svg'
import { ReactComponent as Close } from 'shared/img/close-icon.svg'

import { localeProfileKeysType } from 'shared/locale/en-US'
import { getRouteParamsByStrings } from 'shared/utils/getRouteParamsByStrings'

import { logout } from 'features/auth/actions'
import { selectIsUserAuthenticated } from 'features/auth/selectors'
import { selectLocaleDict } from 'features/locale/selectors'

import { useModalContext } from 'components/HOC/withModalQuery/ModalContext'

import Spinner from 'components/Spinner'

import { IProfileMenuLinks, profileMenuLinks } from './data'

import './Profile.scss'

interface IProps {
  path: string
}

interface ITab {
  tab: string
}

const b = block('profile')

export const Profile: React.FC<IProps> = ({ path }) => {
  const { replaceLastModal, closeModal } = useModalContext()
  const dispatch = useDispatch()
  const history = useHistory()
  const params = getRouteParamsByStrings<ITab>(MODAL_ROUTE_PATH.PROFILE, path)!.tab
  const locale = useSelector(selectLocaleDict, shallowEqual).profile

  const isAuth = useSelector(selectIsUserAuthenticated, shallowEqual)

  const [selectedLink, setSelectedLink] = useState<IProfileMenuLinks | null>(null)

  useEffect(() => {
    if (!isAuth) {
      closeModal(MODAL_ROUTE_URLS.PROFILE(path))()
      history.push(ROUTE_URLS.MAIN())
    }
  }, [closeModal, history, isAuth, path])

  useEffect(() => {
    if (profileMenuLinks.find(link => link.title === params) !== undefined) {
      setSelectedLink(profileMenuLinks.find(link => link.title === params)!)
    } else setSelectedLink(profileMenuLinks[0])
  }, [params])

  const onClickBack = () => {
    replaceLastModal(MODAL_ROUTE_PATH.PROFILE)()
  }

  const handleClose = useCallback(() => {
    closeModal(MODAL_ROUTE_URLS.PROFILE(selectedLink?.title || ''))()
  }, [replaceLastModal, selectedLink])

  const componentToRender = useMemo(() => {
    if (selectedLink) {
      if (selectedLink.Component) {
        return (
          <Suspense fallback={<Spinner isLoading />}>
            <selectedLink.Component />
          </Suspense>
        )
      }
      if (selectedLink.modalRoute) {
        replaceLastModal(selectedLink.modalRoute)()
      }
      if (selectedLink.isLogout) {
        dispatch(logout())
        history.push(ROUTE_URLS.MAIN())
      }
    }
    return <Spinner isLoading />
  }, [selectedLink, replaceLastModal, dispatch, history])

  const visibleHeader = useMemo(
    () => selectedLink && !['menu', 'betHistory'].includes(selectedLink.title),
    [selectedLink],
  )

  return (
    <div className={b('wrapper')}>
      {selectedLink && visibleHeader && (
        <div className={b('header')}>
          <div onClick={onClickBack} className={b('back')}>
            <Arrow />
          </div>
          <div className={b('title')}>{locale[selectedLink.title as localeProfileKeysType]}</div>
          <div onClick={handleClose} className={b('back')}>
            <Close />
          </div>
        </div>
      )}
      <div className={b('content-container')}>{componentToRender}</div>
    </div>
  )
}
